import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { RSButton as RSButtonRaw, RSAlert, type RSButtonProps } from 'reactsymbols-kit';
import Styles from '../../styles/colors';
import ImportModelForm from './ImportModelForm';
import ModelSettingsForm, { type ModelSettings } from '../ModelSettingsForm';

// reactsymbols-kit types `RSButton` as `React.HTMLProps<HTMLButtonElement> &
// RSButtonProps`, whose `value`/`iconName`/`className` reject the `null`
// branches this form passes (`state.creating ? null : 'Create'`, …). Re-type the
// component to the kit props intersected with `ButtonHTMLAttributes` and widen
// the affected props to also accept `null`, matching what the kit forwards at
// runtime. Type-only boundary cast; the runtime component is the same `RSButton`.
const RSButton = RSButtonRaw as unknown as React.ComponentType<
  Omit<RSButtonProps, 'value' | 'iconName' | 'className'> &
    Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'value' | 'className'> & {
      value?: string | null;
      iconName?: string | null;
      className?: string | null;
    }
>;

/** The settings + flags this form edits, mirroring the class's merged state. */
interface CreateModelFormState extends ModelSettings {
  /** The imported (backend-converted) model, or `null` until a file is dropped. */
  imported: unknown;
  /** Whether a create request is in flight. */
  creating: boolean;
  /** Whether the last create attempt failed. */
  error: boolean;
}

/**
 * Form for creating a new model.
 *
 * Navigation used the withRouter shim's history.push; it now uses useNavigate
 * directly.
 */
interface CreateModelFormProps {
  /** Whether this form imports an existing model (vs. creating a new one). */
  import: boolean;
  /** Close the form. */
  onRequestClose: () => void;
  /** Create the project; resolves with the new project id. */
  createProject?: (
    name: string,
    settings: Record<string, unknown>,
    content?: unknown
  ) => Promise<string>;
}

function CreateModelForm({
  import: isImport,
  onRequestClose,
  createProject,
}: CreateModelFormProps) {
  const navigate = useNavigate();

  // The settings + flag fields are updated together via partial merges
  // (onChangeSettings/onImport did this.setState(partial)). Kept in a single
  // object that we spread-merge into, preserving the class's setState merge
  // semantics. Initialised from the class's initial state.
  const [state, setState] = useState<CreateModelFormState>({
    name: '',
    notation: 'MERODE',
    generateMethods: true,
    propagateOnDependency: true,
    propagateOnMethod: true,
    imported: null,
    creating: false,
    error: false,
  });

  // Merge helper reproducing this.setState(partial).
  const update = (change: Partial<CreateModelFormState>) => {
    setState((current) => ({ ...current, ...change }));
  };

  /**
   * Is the form valid?
   */
  const valid = state.name.length > 0 && (!isImport || state.imported !== null);

  /**
   * Create the project
   */
  const onCreate = () => {
    if (!state.creating && createProject) {
      update({ creating: true });
      createProject(
        state.name,
        {
          notation: state.notation,
          generateMethods: state.generateMethods,
          propagateOnDependency: state.propagateOnDependency,
          propagateOnMethod: state.propagateOnMethod,
        },
        state.imported
      )
        .then((projectId) => {
          // Project successfully created. Go there.
          navigate(`/project/${projectId}`);
        })
        .catch(() => {
          // Oops. Signal error to user
          update({ creating: false, error: true });
        });
    }
  };

  /**
   * Form submit handler.
   *
   * Triggered when enter is pushed while the form is focused.
   */
  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (valid && !state.creating) {
      onCreate();
    }
  };

  /**
   * Handler for the embedded import form
   */
  const onImport = (filename: string, content: unknown) => {
    let name = state.name;
    if (filename && name.length === 0) {
      name = filename.replace(/\.[^.]*$/, '');
    }
    update({ name: name, imported: content });
  };

  /**
   * Handler for the embedded ModelSettingsForm
   */
  const onChangeSettings = (change: Partial<ModelSettings>) => {
    update(change);
  };

  /**
   * Render the import for if needed
   */
  const renderImportForm = () => {
    if (isImport) {
      let children;

      if (state.imported === null) {
        children = <ImportModelForm onImport={onImport} />;
      } else {
        children = (
          <RSAlert visible level="success">
            <p>Your file was succesfully converted.</p>
          </RSAlert>
        );
      }

      return (
        <div className="row model-import-wrapper">
          <div className="col">{children}</div>
        </div>
      );
    }
  };

  /**
   * Render an error message if something went wrong.
   */
  const renderError = () => {
    if (state.error) {
      return (
        <div className="row model-error">
          <div className="col">
            <RSAlert visible level="danger" requestClose={() => update({ error: false })}>
              <p>Something went wrong creating your model. Please try again.</p>
            </RSAlert>
          </div>
        </div>
      );
    }
  };

  return (
    <div className="merlin-modal create-model-form">
      <div className="title">{isImport ? 'Import a model' : 'Create a new model'}</div>
      <form className="form" onSubmit={onSubmit}>
        <div className="modal-body">
          <div className="container">
            {renderError()}
            {renderImportForm()}
            <ModelSettingsForm
              onChange={onChangeSettings}
              values={{
                name: state.name,
                notation: state.notation,
                generateMethods: state.generateMethods,
                propagateOnMethod: state.propagateOnMethod,
                propagateOnDependency: state.propagateOnDependency,
              }}
              disabled={state.creating}
            />
          </div>
        </div>
        <div className="button-wrapper">
          <RSButton
            value="Cancel"
            type="button"
            background={Styles.defaultGradient}
            color={Styles.darkTextColor}
            onClick={onRequestClose}
          />
          <RSButton
            type="submit"
            background={Styles.blueGradient}
            value={state.creating ? null : 'Create'}
            disabled={!valid}
            iconName={state.creating ? 'FaSpinner' : null}
            iconSize={15}
            className={state.creating ? 'spin' : null}
          />
        </div>
      </form>
    </div>
  );
}

export default CreateModelForm;
